import type { PreferenceValues } from '../contract/types';
import {
  intlIdentifierFor,
  labelFor,
  labelSeparatorFor,
} from '../units/labels';
import type { QuantityKind, UnitFor } from '../units/registry';
import { numberFormat } from './intl-cache';
import { formatNumber } from './number';

/**
 * A number and its unit, as one string.
 *
 * Which of the two paths below runs is invisible to the caller, and it has to be: a table column
 * showing pressure in kPa and one showing distance in km sit next to each other, and a reader
 * should not be able to tell that CLDR knows about one and not the other.
 *
 * The Intl-backed path hands the whole job to one formatter rather than formatting a number and
 * appending a label, because the placement and spacing are themselves locale data — en-US writes
 * `1.5°C` closed up and fr-CA writes `1,5 °C` with a space, and neither is something this package
 * should be deciding.
 */

export type MeasurementFormatOptions<K extends QuantityKind> = {
  readonly kind: K;
  /** The unit the value is already in. Conversion happens before this is called. */
  readonly unit: UnitFor<K>;
  /** Fixed fractional width, so a column of figures lines up. */
  readonly digits: number;
};

/**
 * Formats an already-converted measurement.
 *
 * Non-finite input is passed through to `Intl`, which renders `NaN` and `∞`, matching
 * {@link formatNumber}. That is deliberate rather than an oversight: `Infinity` is the honest
 * answer for the fuel economy of a vehicle that burned nothing, and whether a reader should see it
 * or an empty cell depends on what the cell is for. The components in C6 own that choice.
 *
 * @param preferences - Supplies the locale.
 * @param value - The number, already in `options.unit`.
 * @param options - The quantity, its unit and the digit width.
 * @returns The formatted measurement, such as `80 mi` or `32 psi`.
 */
export const formatMeasurement = <K extends QuantityKind>(
  preferences: PreferenceValues,
  value: number,
  { kind, unit, digits }: MeasurementFormatOptions<K>
): string => {
  const { locale } = preferences;
  const identifier = intlIdentifierFor(kind, unit);

  if (identifier === null) {
    const number = formatNumber(preferences, value, { digits });
    const separator = labelSeparatorFor(locale);

    return `${number}${separator}${labelFor(locale, kind, unit)}`;
  }

  return numberFormat(locale, {
    style: 'unit',
    unit: identifier,
    unitDisplay: 'short',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
};
